import moment from 'moment';
import { formatCPF, formatPlate, formatCurrency } from 'react-data-formatter';
import { primary, warn } from 'styles/colorProvider';

export const REACTIVATION_STATUS = {
  CANCELED: 'canceled',
  AWAITING_SIGN: 'awaiting_sign_reactivation',
  AWAITING_TERM: 'awaiting_term_reactivation',
  AWAITING_INSPECTION: 'awaiting_inspection_reactivation',
  INSPECT_APPROVED: 'inspect_approved',
  INSPECT_APPROVED_WITH_NOTES: 'inspect_approved_with_notes',
  AWAITING_PAYMENT_REACTIVATION: 'awaiting_payment_reactivation',
  AWAITING_PAID: 'awaiting_paid_reactivation',
  AWAITING_ACCEPTANCE: 'awaiting_acceptance_reactivation',
};

export const REACTIVATION_STAGE = {
  INITIAL: 'initial',
  AWAITING_TERM: 'awaiting_term',
  SIGNED_TERM_UPLOADED: 'signed_term_uploaded',
  AWAITING_PAYMENT: 'awaiting_payment',
};

export const REACTIVATION_NEXT_STEP = {
  TERM: 'reactivation_term',
  INSPECTION: 'new_inspection',
};

export const REACTIVATION_UPLOADED_DOCUMENT_TYPE = {
  TERM: 'term',
  CNH: 'cnh',
};

export const REACTIVATION_ENDPOINTS = {
  list: '/reactivations',
  export: '/reactivations/export',
  currentReactivation: (contractId) => `/contracts/${contractId}/reactivation/current`,
  start: (contractId) => `/contracts/${contractId}/reactivation`,
  downloadTerm: (contractId) => `/contracts/${contractId}/reactivation/term`,
  uploadSignedTerm: (policyId) => `/contracts/${policyId}/reactivation/upload`,
  requestReactivationInspection: (contractId) => `/contracts/${contractId}/reactivation/inspection`,
  movements: (policyNumber) => `/reactivation/movements/${policyNumber}`,
  downloadReactivationUploadedDocument: (contractId, type) => `/contracts/${contractId}/reactivation/documents/${type}`,
  confirmReactivation: (contractId) => `/contracts/${contractId}/reactivation/confirm`,
  reactivationSubscriptionInvoice: (contractId) => `/contracts/${contractId}/reactivation/subscription-invoice`,
};

const readStatusFromObject = (obj) => {
  if (obj == null || typeof obj !== 'object') return null;
  const s =
    obj.currentstatus ??
    obj.currentStatus ??
    obj.CurrentStatus ??
    obj.current_status ??
    obj.status ??
    obj.policyStatus ??
    obj.policy_status;
  if (s == null || s === '') return null;
  return typeof s === 'string' ? s.trim() : String(s);
};

export const getReactivationCurrentStatus = (reactivation) => {
  const direct = readStatusFromObject(reactivation);
  if (direct) return direct;
  if (reactivation != null && typeof reactivation === 'object' && reactivation.data != null) {
    return readStatusFromObject(reactivation.data);
  }
  return null;
};

export const reactivationStatusEquals = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
};

export const isReactivationInspectApprovedStep = (status) =>
  reactivationStatusEquals(status, REACTIVATION_STATUS.INSPECT_APPROVED) ||
  reactivationStatusEquals(status, REACTIVATION_STATUS.INSPECT_APPROVED_WITH_NOTES);

export const getReactivationInspectionDisplayCode = (reactivation) => {
  if (reactivation == null || typeof reactivation !== 'object') return '';
  const candidates = [
    reactivation.inspection_code,
    reactivation.inspectionCode,
    reactivation?.inspection?.inspection_code,
    reactivation?.inspection?.inspectionCode,
    reactivation.inspection_protocol,
    reactivation.protocol,
    reactivation?.inspection?.protocol,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return '';
};

export const getReactivationInspectionLink = (reactivation) => {
  if (reactivation == null || typeof reactivation !== 'object') return '';
  const candidates = [
    reactivation.inspection_url,
    reactivation.inspectionUrl,
    reactivation.link_vistoria,
    reactivation.linkVistoria,
    reactivation?.inspection?.inspection_url,
    reactivation?.inspection?.inspectionUrl,
    reactivation?.inspection?.url,
    reactivation?.inspection?.link,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return '';
};

const isNonEmptyLinkString = (s) => typeof s === 'string' && s.trim().length > 0;

const findNestedHttpUrl = (value, depth = 0) => {
  if (depth > 8 || value == null) return '';
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return '';
    if (/^https?:\/\//i.test(t)) return t;
    return '';
  }
  if (typeof value !== 'object') return '';
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      const f = findNestedHttpUrl(value[i], depth + 1);
      if (f) return f;
    }
    return '';
  }
  const keys = Object.keys(value);
  for (let i = 0; i < keys.length; i += 1) {
    const f = findNestedHttpUrl(value[keys[i]], depth + 1);
    if (f) return f;
  }
  return '';
};

export const parseReactivationInspectionLinkResponse = (data) => {
  if (data == null) return '';
  if (isNonEmptyLinkString(data)) return data.trim();

  if (typeof data !== 'object') return '';

  const candidates = [
    data.url,
    data.URL,
    data.href,
    data.link,
    data.inspection_url,
    data.inspectionUrl,
    data.inspection_link,
    data.inspectionLink,
    data.link_vistoria,
    data.linkVistoria,
    data.vistoria_url,
    data.vistoriaUrl,
    data.inspection,
    data.result,
    data.payload,
    data?.data?.url,
    data?.data?.URL,
    data?.data?.link,
    data?.data?.inspection_url,
    data?.data?.inspectionUrl,
    data?.data?.href,
    data?.reactivation?.inspection_url,
    data?.reactivation?.url,
    data?.inspection?.inspection_url,
    data?.inspection?.url,
    data?.inspection?.link,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (isNonEmptyLinkString(c)) return c.trim();
  }

  if (data.data != null && data.data !== data) {
    const inner = parseReactivationInspectionLinkResponse(data.data);
    if (inner) return inner;
  }

  const nested = findNestedHttpUrl(data);
  return nested || '';
};

const POLICY_REACTIVATION_STATUS_DISPLAY = {
  canceled: { label: 'Não iniciada', color: warn },
  awaiting_term_reactivation: { label: 'Iniciado - Gerar termo reativação', color: warn },
  awaiting_sign_reactivation: { label: 'Aguardando assinatura termo reativação', color: warn },
  awaiting_acceptance_reactivation: { label: 'Aguardando aceitação da reativação', color: warn },
  awaiting_acceptance_term_reactivation: { label: 'Aguardando aceitação do termo reativação', color: warn },
  awaiting_inspection_reactivation: { label: 'Aguardando vistoria reativação', color: warn },
  [REACTIVATION_STATUS.INSPECT_APPROVED]: { label: 'Vistoria aprovada — confirmar reativação', color: warn },
  [REACTIVATION_STATUS.INSPECT_APPROVED_WITH_NOTES]: { label: 'Vistoria aprovada (obs.) — confirmar reativação', color: warn },
  inspect_rejected: { label: 'Vistoria reativacao - reprovada', color: warn },
  reactivation_term_expired: { label: 'Prazo do termo de reativação expirado', color: warn },
  awaiting_payment_reactivation: { label: 'Aguardando pagamento reativação', color: warn },
  awaiting_paid_reactivation: { label: 'Aguardando pagamento reativação', color: warn },
  rejected_reactivation: { label: 'Não iniciada reativação', color: warn },
};

export const formatPolicyReactivationStatus = (status) => {
  if (!status) return;
  const key = String(status).trim().toLowerCase();
  return POLICY_REACTIVATION_STATUS_DISPLAY[key] ?? { label: status, color: warn };
};

export const getReactivationStage = (policy) => {
  if (
    policy?.status === REACTIVATION_STATUS.AWAITING_PAID ||
    policy?.status === REACTIVATION_STATUS.AWAITING_PAYMENT_REACTIVATION
  )
    return REACTIVATION_STAGE.AWAITING_PAYMENT;
  if (policy?.status === REACTIVATION_STATUS.AWAITING_INSPECTION || policy?.reactivation_signed_term_uploaded)
    return REACTIVATION_STAGE.SIGNED_TERM_UPLOADED;
  if (policy?.status === REACTIVATION_STATUS.AWAITING_SIGN || policy?.reactivation_started)
    return REACTIVATION_STAGE.AWAITING_TERM;
  return REACTIVATION_STAGE.INITIAL;
};

export const normalizeMovements = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.logs)) return data.logs;
  if (Array.isArray(data?.movements)) return data.movements;
  if (Array.isArray(data?.history)) return data.history;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

export const getStatusLabel = (status) => formatPolicyReactivationStatus(status)?.label || status || '-';

export const getMovementDateValue = (movement) =>
  new Date(movement?.data_criacao || movement?.created_at || movement?.date || movement?.updated_at || 0).getTime();

const formatStartDate = (value) => {
  const formatted = moment.utc(value).format('DD/MM/YYYY');
  return formatted === 'Invalid date' ? '-' : formatted;
};

export const dataColumns = [
  {
    name: 'ID',
    selector: (row) => row?.id || '-',
    sortable: true,
    width: '7%',
  },
  {
    name: 'Contrato',
    cell: (row) => (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          window.open(`/backoffice/detalhes-do-contrato/${row?.id}`, '_blank');
        }}
        style={{
          background: 'transparent',
          border: 'none',
          color: primary,
          fontWeight: 700,
          cursor: 'pointer',
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        {row?.policy_number || '-'}
      </button>
    ),
    sortable: true,
    width: '10%',
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
  },
  {
    name: 'Parceiro',
    selector: (row) => row?.parceiro || '-',
    sortable: true,
    width: '10%',
  },
  {
    name: 'Cliente',
    selector: (row) => row?.cliente || '-',
    sortable: true,
    width: '15%',
  },
  {
    name: 'CPF',
    selector: (row) => (row?.cpf ? formatCPF(row.cpf) : '-'),
    sortable: true,
    width: '12%',
  },
  {
    name: 'Placa',
    selector: (row) => formatPlate(row?.placa) || 'Sem placa',
    sortable: true,
    width: '10%',
  },
  {
    name: 'Data Início',
    selector: (row) => formatStartDate(row?.data_inicio_contrato),
    sortable: true,
    width: '10%',
  },
  {
    name: 'Status Atual',
    selector: (row) => {
      const s = row?.status_reativacao || 'canceled';
      return formatPolicyReactivationStatus(s)?.label || s || '-';
    },
    sortable: true,
    width: '10%',
  },
  {
    name: 'Valor',
    selector: (row) => formatCurrency(row?.valor || 0),
    sortable: true,
    width: '10%',
  },
];
